import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, SimpleChanges, OnChanges } from '@angular/core';
import { AccordionConfig, AccordionItem } from './accordion-info.interface';

@Component({
  selector: 'usb-accordion-info-product',
  templateUrl: './accordion-info-product.component.html',
  styleUrls: ['./accordion-info-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccordionInfoProductComponent {
  @Input() items: AccordionItem[] = [];
  @Input() categoryId: number = 0;
  @Input() infoRoom: boolean = false;
  @Input() config: AccordionConfig = {
    multiple: false,
    animated: true,
    showArrows: true,
    borderTop: true,
    closeOthers: true,
    defaultOpen: []
  };

  @Output() itemToggle = new EventEmitter<{ item: AccordionItem; isOpen: boolean; index: number }>();

  @ContentChild('accordionContent') customContentTemplate!: TemplateRef<any>;

  private openItemsArray: (string | number)[] = [];

  constructor(private cdr: ChangeDetectorRef, public el: ElementRef) {
    if (this.config.defaultOpen) {
      this.openItemsArray = [...this.config.defaultOpen];
    }
  }

  // ✅ Arrow function para mantener el contexto
  isOpen(itemId: string | number): boolean {
    return this.openItemsArray.includes(itemId);
  }

  // ✅ Arrow function para mantener el contexto
  toggleItem(item: AccordionItem, index: number): void {
    if (item.disabled) return;

    const itemId = item.id || 0;
    const isCurrentlyOpen = this.isOpen(itemId);
    let newOpenItems: (string | number)[];

    if (this.config.multiple) {
      if (isCurrentlyOpen) {
        newOpenItems = this.openItemsArray.filter(id => id !== item.id);
      } else {
        newOpenItems = [...this.openItemsArray, item.id];
      }
    } else {
      if (isCurrentlyOpen) {
        newOpenItems = [];
      } else {
        newOpenItems = [item.id];
      }
    }

    this.openItemsArray = newOpenItems;
    this.cdr.markForCheck();

    this.itemToggle.emit({
      item,
      isOpen: !isCurrentlyOpen,
      index
    });
  }

  // ✅ Arrow function para mantener el contexto
  openItem = (itemId: string | number): void => {
    if (this.config.multiple) {
      if (!this.openItemsArray.includes(itemId)) {
        this.openItemsArray = [...this.openItemsArray, itemId];
        this.cdr.markForCheck();
      }
    } else {
      this.openItemsArray = [itemId];
      this.cdr.markForCheck();
    }
  }

  // ✅ Arrow function para mantener el contexto
  closeItem = (itemId: string | number): void => {
    this.openItemsArray = this.openItemsArray.filter(id => id !== itemId);
    this.cdr.markForCheck();
  }

  // ✅ Arrow function para mantener el contexto
  toggleByIndex = (index: number): void => {
    const item = this.items[index];
    if (item) {
      this.toggleItem(item, index);
    }
  }

  // ✅ Arrow function para mantener el contexto
  closeAll = (): void => {
    this.openItemsArray = [];
    this.cdr.markForCheck();
  }

  // ✅ Arrow function para mantener el contexto
  openAll = (): void => {
    if (this.config.multiple) {
      this.openItemsArray = this.items.map(item => item.id);
      this.cdr.markForCheck();
    }
  }

  paddingAccordion(paddingX: string): string {
    let padding: string = '0';
    switch(this.categoryId) {
      case 1:
        if(paddingX === 'left') { 
          padding = this.infoRoom ? '0' : '3.25em';
        }
        if(paddingX === 'right') {
          padding = this.infoRoom ? '0' : '2.8em';
        }
        break;
      case 8: 
        padding = '.75em'; 
        break;
    }
    return padding;
  }

  getItemContentHeight(index: number): number {
    const accordionElement = this.el.nativeElement;
    const items = accordionElement.querySelectorAll('.accordion-item');
    if (items && items[index]) {
      const currentItem = items[index];
      const content = currentItem.querySelector('.accordion-content');
      if (content) {
        return content.scrollHeight;
      }
    }
    return 0;
  }

  getPoliciesHeight(): number {
    const accordionElement = this.el.nativeElement;
    const items = accordionElement.querySelectorAll('.accordion-item');
    for (let i = 0; i < items.length; i++) {
      const title = items[i].querySelector('.accordion-title')?.textContent;
      if (title?.toLowerCase().includes('política') || title?.toLowerCase().includes('cancelación')) {
        const content = items[i].querySelector('.accordion-content');
        if (content) {
          return content.scrollHeight;
        }
      }
    }
    const policies = accordionElement.querySelector('usb-cancellation-policies');
    if (policies) {
      return policies.scrollHeight;
    }
    return 0;
  }

  getItemContentHeightById(itemId: string): number {
    const accordionElement = this.el.nativeElement;
    const items = accordionElement.querySelectorAll('.accordion-item');
    for (let i = 0; i < items.length; i++) {
      const header = items[i].querySelector('.accordion-header');
      if (header?.getAttribute('aria-controls') === `accordion-content-${itemId}`) {
        const content = items[i].querySelector('.accordion-content');
        if (content) {
          return content.scrollHeight;
        }
      }
    }
    return 0;
  }
}